/**
 * Report Editor Route (Story 5.31)
 *
 * Route: /reports/:reportId/edit
 *
 * Full-viewport immersive report editor adopting the app's signature visual
 * identity (notch header strip, dot-grid recessed canvas, brand sidebar,
 * page-number badges). Real logic: multi-page state + undo/redo, debounced
 * autosave, zoom (buttons + keyboard + pinch), exit guards, Cmd+K palette.
 *
 * Uses position:fixed z-50 to overlay DashboardLayout for a full-viewport
 * experience without modifying the layout.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowLineLeft, ArrowLineRight, Plus, DotsThreeVertical, PencilSimple, Copy, Trash, CheckCircle, CircleNotch, CloudArrowUp, SquaresFour, ArrowsLeftRight, CaretUp, CaretDown, CaretRight } from "@phosphor-icons/react";
import { CATEGORY_ICON_MAP } from "../../../components/reports/categoryIconMap";
import { apiGet, apiPost, apiPut } from "../../../lib/apiClient";
import { useReportTemplates } from "../../../hooks/useReportTemplates";
import { useReportPages } from "../../../hooks/useReportPages";
import { migrateLayoutToPages } from "@provenleap/types/report-migration";
import {
  extractSectionLabels,
  extractActiveSections,
  extractSectionOrder,
  buildReportHydration,
} from "../../../lib/reportLayout";
import {
  zoomAtPoint as computeZoomAtPoint,
  clampPan as computeClampPan,
} from "../../../lib/canvasPanZoom";
import { buildPagesByCategory } from "../../../lib/pagesByCategory";
import { useDateRange } from "../../../hooks/useDateRange";
import { useDebounce } from "../../../hooks/useDebounce";
import { useClientIntegrations } from "../../../hooks/useClientIntegrations";
import { useBrand } from "../../../hooks/useBrands";
import { unionMemberPlatforms } from "../../../components/reports/rollupPaletteGate";
import { resolveEffectiveClientId, isClientConnected } from "../../../lib/reportClientSource";
import {
  EditorActionsLeft,
  EditorSearchTrigger,
  EditorActionsRight,
  EditorReportTitle,
} from "../../../components/reports/EditorToolbar";
import type { SaveStatus } from "../../../components/reports/EditorToolbar";
import { CanvasEditor, EditorRightSidebar, FloatingCategoryPicker, FloatingWidgetSettings } from "../../../components/reports/CanvasEditor";
import type { CanvasEditorProps } from "../../../components/reports/CanvasEditor";
import { ReportDndProvider } from "../../../components/reports/ReportDndProvider";
import { ACTIVE_CATEGORIES } from "../../../components/reports/WidgetPalette";
import {
  getNotchMarginRight,
  getPaperLeftShift,
  paperShiftForWidth,
  notchMarginForWidth,
  PICKER_PANEL_WIDTH,
} from "../../../components/reports/categoryPickerRegistry";
import { ZoomControls } from "../../../components/reports/ZoomControls";
import { useCanvasZoom, ZOOM_MIN, ZOOM_MAX } from "../../../hooks/useCanvasZoom";
import { getGridDimensions, PAGE_CATEGORIES, DEFAULT_CATEGORY } from "@provenleap/types/report-rendering";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { WidgetType } from "../../../components/reports/WidgetPalette";
import { nextWidgetTitle } from "../../../components/reports/canvasWidgetDefaults";
import { WIDGET_TYPE_INFO } from "../../../components/reports/WidgetConfigPanel";
import { getSmartDefaults, planConfiguredWidgetAdd, planPresetWidgetsAdd } from "../../../lib/gridUtils";
import type { SmartDefaultWidgetType } from "../../../lib/gridUtils";
import type { PresetWidget } from "../../../lib/widgetPresets";
import { PICKER_PLATFORMS, logoUrl } from "../../../lib/integrations";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  Input,
} from "@provenleap/ui";
import { MAX_PAGE_TITLE_LENGTH, PAGE_CATEGORIES as ALL_PAGE_CATEGORIES } from "@provenleap/types/report-rendering";
import type { ReportPage } from "@provenleap/types/report-rendering";
import { INTEGRATION_CATEGORY_MAP } from "../../../components/reports/PageSelectorSidebar";

export interface EditorSearchParams {
  templateId?: string;
  clientId?: string;
  format?: "document" | "slide-deck";
  // Story 40-6 — roll-up identity for a NEW (unsaved) report, carried from the
  // generate wizard so the editor can resolve members + the aggregate palette
  // BEFORE the report is saved. A SAVED roll-up reads these from GET /api/reports/:id
  // instead (loadedReport). Absent ⇒ single-client (byte-identical).
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio";
  brandId?: string;
  /** Comma-joined portfolio member client ids (NEW portfolio roll-up only). */
  members?: string;
}

export const Route = createFileRoute("/_authenticated/reports/$reportId/edit")({
  validateSearch: (search: Record<string, unknown>): EditorSearchParams => ({
    templateId: typeof search.templateId === "string" ? search.templateId : undefined,
    clientId: typeof search.clientId === "string" ? search.clientId : undefined,
    format:
      search.format === "document" || search.format === "slide-deck"
        ? search.format
        : undefined,
    kind:
      search.kind === "client" || search.kind === "rollup" ? search.kind : undefined,
    rollupScope:
      search.rollupScope === "brand" || search.rollupScope === "portfolio"
        ? search.rollupScope
        : undefined,
    brandId: typeof search.brandId === "string" ? search.brandId : undefined,
    members: typeof search.members === "string" ? search.members : undefined,
  }),
  component: ReportEditorPage,
});

/** Minimal client shape read off the shared ["clients"] cache to compute a
 *  roll-up's member-union connectedPlatforms (Story 40-6, AC5). */
interface ClientForUnion {
  id: string;
  platforms?: string[];
}

/** Parse the comma-joined portfolio member param into a bounded id list. */
function parseEditorMemberIds(members: string | undefined): string[] {
  if (!members) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of members.split(",")) {
    const trimmed = id.trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      out.push(trimmed);
    }
  }
  return out;
}

/** Sidebar width constants — used by fit-to-width + zoom-bar offsets. */
const LEFT_SIDEBAR_WIDTH = 220;
const LEFT_SIDEBAR_WIDTH_COLLAPSED = 75;
const RIGHT_SIDEBAR_WIDTH = 165;
const RIGHT_SIDEBAR_WIDTH_COLLAPSED = 75;

/** Viewport width breakpoint for sidebar collapse */
const RESPONSIVE_BREAKPOINT = 1024;


/**
 * Shape returned by `GET /api/reports/:id` (Story 37.1). `layoutJson` is a RAW
 * JSON string (parsed defensively client-side via `safeParseLayout`). `format`
 * is the AUTHORITATIVE top-level column (correction C3); `comparisonStart/End`
 * are nullable ISO date strings restored verbatim into `useDateRange`.
 */
interface LoadedReport {
  id: string;
  clientId: string | null;
  templateId: string | null;
  name: string;
  layoutJson: string | null;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  comparisonStart: string | null;
  comparisonEnd: string | null;
  format: "document" | "slide-deck" | null;
  status: string;
  // Story 40-6 — roll-up discriminator + resolved members (GET /api/reports/:id).
  // A single-client report has kind="client", null scope/brandId, empty members.
  // A roll-up has its CURRENT members resolved server-side (brand = the brand's
  // live members; portfolio = the report_clients junction) so the editor never
  // freezes or re-derives them.
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio" | null;
  brandId?: string | null;
  members?: Array<{ id: string; name: string }>;
  memberClientIds?: string[];
}

function useViewportWidth() {
  const [width, setWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : RESPONSIVE_BREAKPOINT
  );

  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  return width;
}

/**
 * Unified canvas pan + zoom transform model. The canvas content is positioned
 * entirely by `translate(pan.x, pan.y) scale(scale)` (see ZoomWrapper), so there
 * is no scroll/centering offset to fight — zoom-to-cursor and grab-to-pan both
 * just mutate `pan`.
 *
 * - `pan` / `setPan`: React state driving the transform (panRef mirrors it for
 *   synchronous reads inside event handlers).
 * - `ref`: attaches to the canvas container (for cursor styling + bounds).
 * - `onMouseDown`: grab-to-pan on EMPTY area (widgets/controls are left alone).
 * - `zoomAtPoint`: canonical zoom-to-pointer — keeps the content point under the
 *   cursor fixed: contentPt = (cursor - pan) / oldScale; pan = cursor - contentPt*newScale.
 */
function useCanvasPanZoom() {
  const ref = useRef<HTMLElement | null>(null);
  const [pan, setPanState] = useState({ x: 0, y: 0 });
  const panRef = useRef(pan);
  panRef.current = pan;

  // Clamp so the paper can never be dragged fully off-screen: keep at least
  // EDGE px of content visible on every side. The hook reads the live rects;
  // the pure bounds math lives in the tested `canvasPanZoom` leaf (Story 37.9).
  const clampPan = useCallback((next: { x: number; y: number }) => {
    const el = ref.current;
    const wrapper = el?.querySelector("[data-testid='zoom-wrapper']") as HTMLElement | null;
    if (!el || !wrapper) return next;
    const cRect = el.getBoundingClientRect();
    const wRect = wrapper.getBoundingClientRect();
    return computeClampPan(next, cRect, wRect);
  }, []);

  const setPan = useCallback((next: { x: number; y: number }) => {
    const clamped = clampPan(next);
    panRef.current = clamped;
    setPanState(clamped);
  }, [clampPan]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // left button only
    const el = ref.current;
    if (!el) return;
    const target = e.target as HTMLElement;
    // Don't hijack a widget drag/resize or any interactive control.
    if (
      target.closest(".react-grid-item") ||
      target.closest(
        "button, a, input, textarea, select, [role='button'], [contenteditable='true']"
      )
    ) {
      return;
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const start = { ...panRef.current };
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";

    const onMove = (ev: MouseEvent) => {
      setPan({ x: start.x + (ev.clientX - startX), y: start.y + (ev.clientY - startY) });
    };
    const onUp = () => {
      if (ref.current) {
        ref.current.style.cursor = "";
        ref.current.style.userSelect = "";
      }
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [setPan]);

  // Canonical zoom-to-pointer. clientX/Y = viewport coords of the focal point.
  // Returns the new pan so the caller can also commit the new zoom level. The
  // hook reads the live rect; the pure cursor-anchoring math lives in the tested
  // `canvasPanZoom` leaf (Story 37.9) — behavior-preserving delegation.
  const zoomAtPoint = useCallback((oldScale: number, newScale: number, clientX: number, clientY: number) => {
    const el = ref.current;
    if (!el) return panRef.current;
    const rect = el.getBoundingClientRect();
    const next = computeZoomAtPoint(
      oldScale,
      newScale,
      { x: clientX, y: clientY },
      { left: rect.left, top: rect.top },
      panRef.current,
    );
    setPan(next);
    return next;
  }, [setPan]);

  return { ref, pan, setPan, onMouseDown, zoomAtPoint };
}

/**
 * Editable section (category) header. Click to rename; Enter/blur commits,
 * Escape cancels. Displays uppercase but stores the raw entered value.
 */
function SectionHeaderEdit({
  label,
  onSave,
  onDelete,
  canDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: {
  label: string;
  onSave: (label: string) => void;
  onDelete?: () => void;
  canDelete?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isEditing && draft !== label) setDraft(label);

  const startEdit = () => {
    setDraft(label);
    setIsEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== label) onSave(trimmed);
    else setDraft(label);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="px-3 pb-2 pt-3">
        <Input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 60))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(label);
              setIsEditing(false);
            }
          }}
          maxLength={60}
          className="h-6 text-[11px] font-semibold tracking-wider uppercase px-1 py-0"
          data-testid="section-header-input"
        />
      </div>
    );
  }

  return (
    <div className="group/section flex items-center gap-0 px-3 pb-2 pt-3">
      <button
        type="button"
        onClick={startEdit}
        title="Rename section"
        className="min-w-0 truncate text-left text-[11px] font-semibold tracking-wider text-foreground/70 uppercase hover:text-foreground transition-colors cursor-text"
        data-testid="section-header"
      >
        {label}
      </button>
      {/* Options menu — sits right after the name */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            title="Section options"
            aria-label="Section options"
            className="shrink-0 inline-flex h-5 w-5 items-center justify-center rounded text-foreground hover:text-foreground transition-colors cursor-pointer"
            data-testid="section-menu-trigger"
          >
            <DotsThreeVertical size={18} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          sideOffset={6}
          hideArrow
          className="relative overflow-visible rounded-xl p-2 shadow-md w-40"
          style={{ border: "0.5px solid var(--hairline)" }}
        >
          {/* Beak on the LEFT edge, pointing back at the ⋮ trigger. */}
          <div
            className="absolute -left-[5px] top-4 w-2.5 h-2.5 bg-popover rotate-45 z-10"
            style={{ borderLeft: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)" }}
          />
          <DropdownMenuItem
            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
            onClick={startEdit}
            data-testid="section-edit-button"
          >
            <PencilSimple size={14} className="mr-1.5" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
            disabled={!canMoveUp}
            onClick={() => canMoveUp && onMoveUp?.()}
            data-testid="section-move-up-button"
          >
            <CaretUp size={14} className="mr-1.5" />
            Move up
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-11 text-[13px] cursor-pointer rounded-lg"
            disabled={!canMoveDown}
            onClick={() => canMoveDown && onMoveDown?.()}
            data-testid="section-move-down-button"
          >
            <CaretDown size={14} className="mr-1.5" />
            Move down
          </DropdownMenuItem>
          <DropdownMenuItem
            className="min-h-11 text-[13px] cursor-pointer rounded-lg text-destructive focus:text-destructive"
            disabled={!canDelete}
            onClick={() => canDelete && onDelete?.()}
            data-testid="section-delete-button"
          >
            <Trash size={14} className="mr-1.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Single page row — EXACT mockup markup, plus real behavior:
 * click=select, double-click=rename, hover context menu=duplicate/delete,
 * whole row is draggable for reorder (dnd-kit useSortable).
 */
// Layers-tree geometry (px from the page card's left edge). RAIL_LEFT sits under
// the expand caret; rows start after the elbow. Tune these two to align the rail.
const RAIL_LEFT_PX = 24;
const ROWS_LEFT_PX = 42;

function EditorPageRow({
  page,
  pageNumber,
  isActive,
  isSinglePage,
  leftCollapsed,
  onSelect,
  onRename,
  onDuplicate,
  onDelete,
  onChangeCategory,
  selectedWidgetId,
  onSelectWidget,
  onWidgetAction,
}: {
  page: ReportPage;
  pageNumber: number;
  isActive: boolean;
  isSinglePage: boolean;
  leftCollapsed: boolean;
  onSelect: () => void;
  onRename: (title: string) => boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  onChangeCategory: (category: string) => void;
  /** Id of the currently-selected widget (highlights its row in the tree). */
  selectedWidgetId: string | null;
  /** Select a widget from the layers tree (switches page if needed). */
  onSelectWidget: (pageId: string, widgetId: string) => void;
  /** Duplicate/Delete a widget from the layers-tree ⋮ menu. */
  onWidgetAction: (pageId: string, widgetId: string, type: "duplicate" | "delete") => void;
}) {
  // Layers tree expand/collapse — defaults to expanded for the active page so the
  // current page's widgets are visible without an extra click.
  const [expanded, setExpanded] = useState(isActive);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: page.id,
  });
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(page.title);
  const inputRef = useRef<HTMLInputElement>(null);

  const startRename = useCallback(() => {
    setRenameValue(page.title);
    setIsRenaming(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }, [page.title]);

  const commitRename = useCallback(() => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== page.title) onRename(trimmed);
    setIsRenaming(false);
  }, [renameValue, page.title, onRename]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // When the tree is open the page row stops looking like a "button" — no card
  // background, no shadow, no hover fill, no border (the elbow rail is the only
  // affordance). When closed it keeps the full button chrome.
  const treeOpen = expanded && page.widgets.length > 0;

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? "z-50 opacity-80" : ""}`}
    >
      {/* Page row — wrapped so the active-page background overlay covers ONLY the
          row, not the expanded layers tree below it (else the white overlay would
          paint over the tree and hide it). */}
      <div className="relative">
      {isActive && !treeOpen && (
        <div className="absolute inset-0 rounded-lg bg-background shadow-xs pointer-events-none" />
      )}
      <div
        {...attributes}
        {...listeners}
        role="option"
        aria-selected={isActive}
        aria-label={`Page ${pageNumber}: ${page.title}`}
        data-testid={`page-item-${page.id}`}
        onClick={onSelect}
        onDoubleClick={(e) => {
          if (leftCollapsed) return;
          e.stopPropagation();
          startRename();
        }}
        onMouseEnter={(e) => {
          if (isActive) return;
          const badge = e.currentTarget.querySelector<HTMLElement>("[data-page-badge]");
          if (badge) badge.style.borderColor = "hsl(214 25% 70% / 0)";
        }}
        onMouseLeave={(e) => {
          if (isActive) return;
          const badge = e.currentTarget.querySelector<HTMLElement>("[data-page-badge]");
          if (badge) badge.style.borderColor = "hsl(214 25% 55% / 0.7)";
        }}
        className={`group relative z-10 block w-full text-left rounded-lg text-sm font-medium text-foreground border-[0.5px] cursor-pointer ${
          treeOpen ? "" : "hover:bg-background hover:shadow-xs"
        }`}
        style={{
          // Border shown both open and closed; only hidden on the collapsed sidebar
          // for an inactive page. Eased so it fades in/out smoothly.
          borderColor: leftCollapsed && !isActive ? "transparent" : "hsl(var(--input))",
          transition: "border-color 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease",
        }}
      >
        <div
          className={`flex items-center ${leftCollapsed ? "py-2" : "py-1.5"}`}
          style={{ paddingLeft: 8, paddingRight: leftCollapsed ? 0 : 18 }}
        >
          {/* Expand/collapse caret for the layers tree. Always rendered (when the
              sidebar is open) so every page row is the same size/alignment — but a
              page with no widgets has nothing to expand, so its caret is dimmed and
              inert. Toggling doesn't select the page. */}
          {!leftCollapsed && (() => {
            const hasWidgets = page.widgets.length > 0;
            return (
              <button
                type="button"
                data-testid={`page-expand-${page.id}`}
                aria-label={hasWidgets ? (expanded ? `Collapse ${page.title} widgets` : `Expand ${page.title} widgets`) : `${page.title} has no widgets`}
                aria-expanded={hasWidgets ? expanded : undefined}
                disabled={!hasWidgets}
                onClick={(e) => { e.stopPropagation(); if (hasWidgets) setExpanded((v) => !v); }}
                onPointerDown={(e) => e.stopPropagation()}
                className={`-ml-1 mr-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded text-muted-foreground ${
                  hasWidgets ? "hover:text-foreground cursor-pointer" : "opacity-40 cursor-default"
                }`}
              >
                {hasWidgets && expanded ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
              </button>
            );
          })()}

          {/* Page number badge — identical to mockup */}
          <span
            data-page-badge
            className="shrink-0 inline-flex items-center justify-center rounded-md font-semibold tabular-nums"
            style={{
              width: 22,
              height: 22,
              fontSize: isActive ? 13 : 13,
              color: isActive ? "hsl(359 100% 71%)" : "hsl(215 25% 45%)",
              borderWidth: "0.5px",
              borderStyle: "solid",
              borderColor: isActive ? "hsl(214 25% 70% / 0)" : "hsl(214 25% 55% / 0.7)",
              transition: "border-color 0.25s ease, color 0.2s ease, font-size 0.2s ease",
            }}
          >
            {pageNumber}
          </span>


          {isRenaming && !leftCollapsed ? (
            <Input
              ref={inputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitRename();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setIsRenaming(false);
                }
              }}
              maxLength={MAX_PAGE_TITLE_LENGTH}
              className="h-6 text-sm px-1 py-0 flex-1 min-w-0"
              data-testid={`page-rename-input-${page.id}`}
              onClick={(e) => e.stopPropagation()}
              style={{ marginLeft: 9 }}
            />
          ) : (
            <span
              className="whitespace-nowrap overflow-hidden text-ellipsis flex-1 min-w-0 text-[13.5px]"
              style={{
                opacity: leftCollapsed ? 0 : 1,
                marginLeft: leftCollapsed ? 9 : 4,
                transition: "opacity 0.2s ease, margin-left 0.2s ease",
              }}
            >
              {page.title}
            </span>
          )}

          {/* Context menu — appears on hover, absolute so it never shifts layout */}
          {!leftCollapsed && !isRenaming && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="absolute top-1/2 -translate-y-1/2 z-20 text-foreground hover:text-foreground rounded cursor-pointer focus:outline-none focus-visible:outline-none focus:ring-0"
                  style={{ right: -16 }}
                  aria-label={`Options for ${page.title}`}
                  data-testid={`page-menu-trigger-${page.id}`}
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <DotsThreeVertical size={18} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="right"
                align="start"
                sideOffset={6}
                hideArrow
                className="relative z-[70] overflow-visible rounded-xl p-2 shadow-md w-40"
                style={{ border: "0.5px solid var(--hairline)" }}
              >
                {/* Beak on the LEFT edge, pointing back at the ⋮ trigger. */}
                <div
                  className="absolute -left-[5px] top-4 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                  style={{ borderLeft: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)" }}
                />
                <DropdownMenuItem
                  className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    startRename();
                  }}
                >
                  <PencilSimple size={14} className="mr-1.5" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicate();
                  }}
                >
                  <Copy size={14} className="mr-1.5" />
                  Duplicate
                </DropdownMenuItem>

                {/* Move to another section/category */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="min-h-11 text-[13px] cursor-pointer rounded-lg">
                    <ArrowsLeftRight size={14} className="mr-1.5" />
                    Move to…
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent
                    className="relative ml-2 rounded-xl p-2 shadow-md w-48 overflow-visible"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  >
                    {/* Left-pointing arrow — aims back at the "Move to…" row */}
                    <div
                      className="absolute -left-[5px] top-4 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                      style={{ borderLeft: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)" }}
                    />
                    {/* Scrollable list — overflow lives here, NOT on the panel, so the arrow isn't clipped */}
                    <div className="max-h-[420px] overflow-y-auto">
                      {ALL_PAGE_CATEGORIES.map((cat) => {
                        const CatIcon = CATEGORY_ICON_MAP[cat.iconName] ?? SquaresFour;
                        const isCurrent = (page.category ?? "summary") === cat.id;
                        return (
                          <DropdownMenuItem
                            key={cat.id}
                            disabled={isCurrent}
                            className="min-h-11 text-[13px] cursor-pointer rounded-lg data-[disabled]:opacity-100"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isCurrent) onChangeCategory(cat.id);
                            }}
                          >
                            <CatIcon size={14} className="mr-1.5" />
                            {cat.label}
                            {isCurrent && (
                              <span className="ml-auto text-[11px] text-brand">Current</span>
                            )}
                          </DropdownMenuItem>
                        );
                      })}
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {!isSinglePage && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="min-h-11 text-[13px] cursor-pointer rounded-lg text-destructive focus:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                    >
                      <Trash size={14} className="mr-1.5" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      </div>

      {/* Layers tree — the page's widgets (icon + type + title). Click selects the
          widget on the canvas (switching to this page first if needed). Hidden when
          the sidebar is collapsed or the page has no widgets. Animates open/closed
          (height + opacity) via AnimatePresence. */}
      <AnimatePresence initial={false}>
      {!leftCollapsed && expanded && page.widgets.length > 0 && (
        // Elbow-connector tree: a vertical rail on the left, each widget joined to
        // it by an L-shaped elbow. The rail lives per-item (a vertical segment +
        // horizontal elbow), so the last item's segment stops at its own elbow.
        <motion.ul
          key="layers-tree"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
          className="mt-1.5 mb-2 space-y-1 overflow-hidden"
          style={{ paddingLeft: ROWS_LEFT_PX }}
        >
          {page.widgets.map((w, wi) => {
            const info = WIDGET_TYPE_INFO[w.type];
            const Icon = info?.icon;
            // When the widget's title is cleared, the row would render blank — fall
            // back to the widget-type label (e.g. "KPI Card") shown muted, so the
            // layers tree never shows an empty, unidentifiable row.
            const hasTitle = typeof w.title === "string" && w.title.trim().length > 0;
            const layerLabel = hasTitle ? w.title : (info?.label ?? "Widget");
            const isWidgetSelected = w.id === selectedWidgetId;
            const isLast = wi === page.widgets.length - 1;
            const isFirst = wi === 0;
            // The FIRST item's vertical segment starts ABOVE the li (negative top)
            // so the rail visually climbs up to the expand caret instead of starting
            // at the first widget. ~28px ≈ mt-1.5 + half the page card height.
            const railTop = isFirst ? -28 : 0;
            // Rail x-position relative to the widget li (which starts at ROWS_LEFT_PX
            // from the card edge). RAIL_LEFT_PX is measured from the card edge to sit
            // under the expand caret; the elbow bridges rail → row.
            const connectorLeft = RAIL_LEFT_PX - ROWS_LEFT_PX;
            const elbowWidth = ROWS_LEFT_PX - RAIL_LEFT_PX;
            return (
              <li key={w.id} className="group/layer relative">
                {/* Connector: a vertical rail (under the caret) + a horizontal elbow.
                    The elbow is centred on the row (top:50% of the li == button
                    centre, regardless of row height); the rail climbs to the caret
                    on the first item and stops at the elbow centre on the last. */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute w-px bg-input"
                  // Non-last segments extend by the 4px row gap (space-y-1) so the
                  // rail stays continuous across the spacing; the last stops at its elbow.
                  style={{ left: connectorLeft, top: railTop, height: isLast ? `calc(50% - ${railTop}px)` : `calc(100% - ${railTop}px + 4px)` }}
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute h-px bg-input"
                  style={{ left: connectorLeft, top: "50%", width: elbowWidth }}
                />
                <button
                  type="button"
                  data-testid={`layer-item-${w.id}`}
                  aria-selected={isWidgetSelected}
                  onClick={(e) => { e.stopPropagation(); onSelectWidget(page.id, w.id); }}
                  className={`flex w-full items-center justify-start gap-1.5 rounded-lg pl-2.5 pr-2.5 py-1.5 text-left text-[12.5px] border-[0.5px] cursor-pointer transition-colors ${
                    isWidgetSelected
                      ? "bg-background shadow-xs text-foreground"
                      : "border-transparent text-foreground/70 hover:bg-background hover:border-input hover:text-foreground group-hover/layer:bg-background group-hover/layer:border-input group-hover/layer:text-foreground"
                  }`}
                  // Selected: border the SAME colour + 1px weight as the tree rail
                  // (bg-input) so the two read identically (a 0.5px border looks lighter).
                  style={isWidgetSelected ? { borderColor: "var(--hairline)", borderWidth: 1 } : undefined}
                >
                  {Icon && <Icon size={14} className={`shrink-0 ${isWidgetSelected ? "text-foreground" : "text-muted-foreground"}`} />}
                  <span className={`truncate ${hasTitle ? "" : "italic text-muted-foreground"}`}>{layerLabel}</span>
                </button>

                {/* ⋮ menu — Duplicate / Delete for this widget. Shown on row hover
                    (and while its menu is open); sits just inside the row's right
                    edge, close to the text. */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid={`layer-menu-trigger-${w.id}`}
                      aria-label={`Options for ${layerLabel}`}
                      onClick={(e) => e.stopPropagation()}
                      onPointerDown={(e) => e.stopPropagation()}
                      style={{ right: -6 }}
                      className="absolute top-1/2 -translate-y-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-lg text-muted-foreground hover:text-foreground transition-[color,opacity] cursor-pointer focus:outline-none focus-visible:outline-none opacity-0 group-hover/layer:opacity-100 data-[state=open]:opacity-100"
                    >
                      <DotsThreeVertical size={16} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    side="right"
                    align="start"
                    sideOffset={6}
                    hideArrow
                    className="relative z-[70] overflow-visible rounded-xl p-2 shadow-md w-40"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  >
                    {/* Beak on the LEFT edge, pointing back at the ⋮ trigger. */}
                    <div
                      className="absolute -left-[5px] top-4 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                      style={{ borderLeft: "0.5px solid var(--hairline)", borderBottom: "0.5px solid var(--hairline)" }}
                    />
                    <DropdownMenuItem
                      className="min-h-9 text-[13px] cursor-pointer rounded-lg"
                      onClick={(e) => { e.stopPropagation(); onWidgetAction(page.id, w.id, "duplicate"); }}
                    >
                      <Copy size={14} className="mr-1.5" />
                      Duplicate
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="min-h-9 text-[13px] cursor-pointer rounded-lg text-destructive focus:text-destructive"
                      onClick={(e) => { e.stopPropagation(); onWidgetAction(page.id, w.id, "delete"); }}
                    >
                      <Trash size={14} className="mr-1.5" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            );
          })}
        </motion.ul>
      )}
      </AnimatePresence>
    </li>
  );
}

function ReportEditorPage() {
  const { reportId } = Route.useParams();
  const {
    templateId,
    clientId,
    format: formatParam,
    kind: kindParam,
    rollupScope: rollupScopeParam,
    brandId: brandIdParam,
    members: membersParam,
  } = Route.useSearch();
  const navigate = useNavigate();
  const viewportWidth = useViewportWidth();

  // Left sidebar (pages) collapse state. The right widget palette manages its own
  // collapse internally inside EditorRightSidebar.
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const rightCollapsed = true; // EditorRightSidebar opens collapsed by default

  const leftWidth = leftCollapsed ? LEFT_SIDEBAR_WIDTH_COLLAPSED : LEFT_SIDEBAR_WIDTH;

  // Grab-to-pan + zoom-to-cursor (unified translate()scale() transform model).
  const canvasPan = useCanvasPanZoom();

  // Initial centering: with no margin:auto, content starts at translate(0,0)
  // (top-left). Once mounted, center the paper horizontally + a small top gap.
  const hasInitialPanRef = useRef(false);
  useEffect(() => {
    if (hasInitialPanRef.current) return;
    const container = canvasPan.ref.current;
    const wrapper = document.querySelector("[data-testid='zoom-wrapper']") as HTMLElement | null;
    if (!container || !wrapper) return;
    const cRect = container.getBoundingClientRect();
    const wRect = wrapper.getBoundingClientRect();
    if (cRect.width === 0 || wRect.width === 0) return; // not laid out yet
    hasInitialPanRef.current = true;
    // wRect is already scaled; center it in the container, with a 48px top gap.
    canvasPan.setPan({ x: Math.max(24, (cRect.width - wRect.width) / 2), y: 48 });
  });

  // Editor state
  const [reportName, setReportName] = useState("Untitled Report");
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("unsaved");
  const [showExitDialog, setShowExitDialog] = useState(false);
  // Format is seeded from the search param for NEW reports, then overridden by
  // the loaded report's authoritative top-level `format` column on hydrate
  // (Story 37.1, correction C3). `layoutJson.format` is advisory and ignored.
  const [selectedFormat, setSelectedFormat] = useState<"document" | "slide-deck">(formatParam ?? "document");
  const [activeCategory, setActiveCategory] = useState<string | null>(ACTIVE_CATEGORIES[0]!.id);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const [selectedWidget, setSelectedWidget] = useState<WidgetConfig | null>(null);
  // CanvasEditor owns the canvas selection; it registers a deselect fn here so the
  // right-sidebar settings "Back" button can clear it (Epic 37).
  const deselectWidgetRef = useRef<() => void>(() => {});
  // …and a symmetric select-by-id fn so the pages-sidebar layers tree can select a
  // widget on the canvas. When the target widget is on a DIFFERENT page, we switch
  // the active page first and stash the id in pendingSelectRef — the page-change
  // effect performs the select AFTER the canvas re-renders with that page's widgets.
  const selectWidgetRef = useRef<(widgetId: string) => void>(() => {});
  const pendingSelectRef = useRef<string | null>(null);
  // Imperative duplicate/delete (reusing the canvas's tested handlers) for the
  // layers-tree ⋮ menu. Cross-page actions defer until the target page is active.
  const widgetActionsRef = useRef<{ duplicate: (id: string) => void; delete: (id: string) => void }>({
    duplicate: () => {},
    delete: () => {},
  });
  const pendingWidgetActionRef = useRef<{ type: "duplicate" | "delete"; widgetId: string } | null>(null);

  // The widget editor and the category picker share the same right slot, so only
  // ONE is open at a time — opening one closes the other:
  //  • selecting a widget (editor opens) → close the picker (activeCategory = null)
  //  • opening a category (picker opens) → deselect the widget (editor closes)
  const handleSelectedWidgetChange = useCallback((widget: WidgetConfig | null) => {
    setSelectedWidget(widget);
    if (widget) setActiveCategory(null);
  }, []);
  const handleSetActiveCategory = useCallback((id: string | null) => {
    setActiveCategory(id);
    if (id !== null) deselectWidgetRef.current();
  }, []);

  // Zoom state (Story 5.38) — default to 80% on open.
  const canvasZoom = useCanvasZoom({ initialZoomLevel: 80 });

  // Right widget sidebar width (icons column). The integration picker now floats
  // as an absolute overlay over the canvas, so it no longer affects column width.
  const rightSidebarWidth = rightCollapsed ? RIGHT_SIDEBAR_WIDTH_COLLAPSED : RIGHT_SIDEBAR_WIDTH;
  const rightColumnWidth = rightSidebarWidth;

  // Compute available width for fit-to-width calculation.
  const computeFitToWidth = useCallback(() => {
    const dims = getGridDimensions(selectedFormat);
    const canvasPadding = 64; // 32px each side
    const availableWidth = viewportWidth - leftWidth - rightSidebarWidth - canvasPadding;
    canvasZoom.fitToWidth(availableWidth, dims.contentWidthPx);
  }, [viewportWidth, leftWidth, rightSidebarWidth, selectedFormat, canvasZoom]);

  const hasInitialZoom = useRef(false);

  // Template loading
  const { templates, isLoading: templatesLoading } = useReportTemplates();

  const selectedTemplate = useMemo(
    () => (templateId ? templates.find((t) => t.id === templateId) : null),
    [templateId, templates]
  );

  // ── Saved-report loading (Story 37.1) ──────────────────────────────────
  // Loads an existing report so the editor restores everything that was saved
  // (pages, widgets, section labels, empty sections, custom order, comparison
  // dates, format, zoom). Gated on `reportId !== "new"` so a brand-new report
  // never fires this. `layoutJson` arrives as a RAW JSON STRING — it is parsed
  // defensively at hydrate time (Task 5 / safeParseLayout), NOT here.
  // Loaded BEFORE the client query so `effectiveClientId` (below) can fall back
  // to `loadedReport.clientId` and re-key the client + integrations queries on
  // it (Story 37.8, blockers #9/#10).
  const queryClient = useQueryClient();
  const isExistingReport = reportId !== "new";
  const {
    data: loadedReport,
    isLoading: reportLoading,
    isError: reportLoadError,
  } = useQuery<LoadedReport>({
    queryKey: ["report", reportId],
    queryFn: async () => {
      const res = await apiGet(`/api/reports/${reportId}`);
      if (!res.ok) throw new Error("Failed to load report");
      const json = await res.json();
      return json.data as LoadedReport;
    },
    enabled: isExistingReport,
  });

  // ── Effective client id (Story 37.8, blocker #9) ───────────────────────
  // `clientId` comes ONLY from the search param, but a saved report opened from
  // the Reports list navigates with no `?clientId=` — so for ANY saved report
  // the search param is undefined and live data would silently fall back to mock
  // for a fully-connected client. `resolveEffectiveClientId` falls back to the
  // client the report ITSELF carries (`loadedReport.clientId`). `loadedReport`
  // is async; the downstream `enabled: !!clientId` guards idle every
  // query/populate until it resolves, so feeding it once loaded is safe. This is
  // the SINGLE source the canvas live-data merge AND the sample-data banner both
  // key on (blocker #10) — see `lib/reportClientSource.ts`.
  const effectiveClientIdRaw = resolveEffectiveClientId(clientId, loadedReport?.clientId);

  // ── Roll-up identity (Story 40-6, AC8) ──────────────────────────────────
  // A report is a ROLL-UP when the URL says so (a NEW report carried from the
  // wizard) OR the loaded report says so (a SAVED roll-up). For a roll-up the
  // editor MUST NOT fall through to the single-client populate / mock path — it
  // branches on `isRollup` everywhere a clientId would otherwise drive data.
  const isRollup = kindParam === "rollup" || loadedReport?.kind === "rollup";
  const rollupScope = rollupScopeParam ?? loadedReport?.rollupScope ?? null;
  const rollupBrandId = brandIdParam ?? loadedReport?.brandId ?? null;

  // CRITICAL: a roll-up has NO effective single client. Forcing `undefined`
  // keeps the single-client client/integration queries + useReportData idle and
  // suppresses the single-client sample-data banner, so a roll-up never renders
  // mock/wrong-client data (Pre-mortem (d)).
  const effectiveClientId = isRollup ? undefined : effectiveClientIdRaw;

  // Resolve the roll-up's member set. SAVED reports get the server-resolved
  // CURRENT members (brand = live members; portfolio = junction). A NEW brand
  // roll-up resolves the brand's members dynamically via useBrand; a NEW
  // portfolio roll-up reads the URL member set. Never frozen for a brand.
  const { data: editorRollupBrand } = useBrand(
    isRollup && rollupScope === "brand" && !loadedReport ? rollupBrandId ?? undefined : undefined,
  );
  const newPortfolioMemberIds = useMemo(
    () => parseEditorMemberIds(membersParam),
    [membersParam],
  );
  const rollupMemberIds = useMemo<string[]>(() => {
    if (!isRollup) return [];
    // SAVED report — trust the server-resolved member set.
    if (loadedReport?.memberClientIds && loadedReport.kind === "rollup") {
      return loadedReport.memberClientIds;
    }
    // NEW report — resolve from the wizard params.
    if (rollupScope === "brand") {
      return (editorRollupBrand?.clients ?? []).map((c) => c.id);
    }
    return newPortfolioMemberIds;
  }, [isRollup, loadedReport, rollupScope, editorRollupBrand, newPortfolioMemberIds]);

  // Client loading — keyed on the EFFECTIVE client id so the display name +
  // sample-data banner resolve on EVERY entry path, including a saved report
  // opened without `?clientId=` (blocker #10).
  const { data: client } = useQuery<{
    id: string;
    name: string;
    logoUrl: string | null;
    industry: string | null;
    businessGoals: string | null;
    notes: string | null;
  }>({
    queryKey: ["clients", effectiveClientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${effectiveClientId}`);
      if (!res.ok) throw new Error("Failed to load client");
      const json = await res.json();
      return json.data;
    },
    enabled: !!effectiveClientId,
  });

  // Set default report name from template + client. A LOADED report's saved
  // name wins (handled in the hydration block below) — this only seeds the name
  // for new reports being created from a template.
  useEffect(() => {
    if (isExistingReport) return; // loaded report sets its own name on hydrate
    if (selectedTemplate && client) {
      setReportName(`${selectedTemplate.name} — ${client.name}`);
    } else if (selectedTemplate) {
      setReportName(selectedTemplate.name);
    }
  }, [selectedTemplate, client, isExistingReport]);

  // Multi-page state with undo/redo (Story 5.32)
  const initialPages = useMemo(
    () => migrateLayoutToPages(selectedTemplate?.parsedLayout ?? null),
    [selectedTemplate]
  );
  // Custom section-label overrides saved in the report layout.
  const initialSectionLabels = useMemo(
    () => extractSectionLabels(selectedTemplate?.parsedLayout),
    [selectedTemplate]
  );
  // Empty sections (exist independently of pages) saved in the report layout.
  // Only the user's own sections are shown; the default "Summary" section is
  // always visible (see pagesByCategory) and holds the pages. No auto-seeding.
  const initialActiveSections = useMemo(
    () => extractActiveSections(selectedTemplate?.parsedLayout),
    [selectedTemplate]
  );
  // Custom section display order saved in the report layout.
  const initialSectionOrder = useMemo(
    () => extractSectionOrder(selectedTemplate?.parsedLayout),
    [selectedTemplate]
  );
  const {
    pages,
    activePageId,
    activePageWidgets,
    setActivePageId,
    addPage,
    deletePage,
    duplicatePage,
    reorderPages,
    renamePage,
    changePageCategory,
    sectionLabels,
    setSectionLabel,
    activeSections,
    addSection,
    deleteSection,
    sectionOrder,
    moveSection,
    updateActivePageWidgets,
    updateActivePageWidgetsSilent,
    undo,
    redo,
    reset: resetPages,
    version,
  } = useReportPages(
    initialPages,
    initialSectionLabels,
    initialActiveSections,
    initialSectionOrder
  );

  // Latest active-page widgets in a ref so `handleAddWidget` can read the
  // current count for its success flag WITHOUT listing `activePageWidgets` in
  // its deps — otherwise the callback identity would churn on every widget edit
  // and bust the floating panels' memoized Add handler (code-review R2). The
  // committing updater (`updateActivePageWidgets`) runs its reducer
  // asynchronously inside React's setState, so a flag captured inside it can't
  // be read before `return`; the ref snapshot is the committed state at click
  // time (single-user editor — no concurrent writer between render and click).
  const activePageWidgetsRef = useRef(activePageWidgets);
  activePageWidgetsRef.current = activePageWidgets;

  // Reset pages when template becomes available.
  // SAVED-WINS-OVER-TEMPLATE (Story 37.1, AC #5): for an EXISTING report
  // (`reportId !== "new"`) the saved-report hydration below is authoritative, so
  // this template reset must NOT fire — otherwise it would clobber the loaded
  // report with template content. The template path therefore runs only for new
  // reports being created from a template.
  const lastResolvedTemplateIdRef = useRef<string | undefined>(undefined);
  const resolvedId = selectedTemplate?.id;
  if (!isExistingReport && resolvedId && resolvedId !== lastResolvedTemplateIdRef.current) {
    lastResolvedTemplateIdRef.current = resolvedId;
    resetPages(
      migrateLayoutToPages(selectedTemplate.parsedLayout),
      extractSectionLabels(selectedTemplate.parsedLayout),
      extractActiveSections(selectedTemplate.parsedLayout),
      extractSectionOrder(selectedTemplate.parsedLayout),
    );

    // Restore zoom level from saved layout (Story 5.38, AC #7); otherwise default to 80%.
    const savedZoom = (selectedTemplate.parsedLayout as unknown as Record<string, unknown>)?.zoomLevel;
    if (typeof savedZoom === "number" && savedZoom >= ZOOM_MIN && savedZoom <= ZOOM_MAX) {
      canvasZoom.setZoom(savedZoom);
      hasInitialZoom.current = true;
    } else {
      canvasZoom.setZoom(80);
      hasInitialZoom.current = false;
    }
  }

  // Clear selected widget when switching pages — UNLESS a layers-tree click asked
  // to select a specific widget on the page we're switching to, in which case we
  // perform that select now (this effect runs after the canvas re-rendered with
  // the new page's widgets, so the id is valid) instead of clearing.
  const prevActivePageIdRef = useRef(activePageId);
  useEffect(() => {
    if (prevActivePageIdRef.current !== activePageId) {
      prevActivePageIdRef.current = activePageId;
      const pending = pendingSelectRef.current;
      pendingSelectRef.current = null;
      const pendingAction = pendingWidgetActionRef.current;
      pendingWidgetActionRef.current = null;
      if (pendingAction) {
        // A ⋮-menu action targeted a widget on the page we just switched to —
        // run it now that the canvas re-rendered with that page's widgets.
        widgetActionsRef.current[pendingAction.type](pendingAction.widgetId);
      } else if (pending) {
        selectWidgetRef.current(pending);
      } else {
        setSelectedWidget(null);
      }
    }
  }, [activePageId]);

  // Select a widget from the pages-sidebar layers tree. Same page → select now;
  // different page → switch page first, then the effect above selects it.
  const handleSelectWidgetFromTree = useCallback(
    (pageId: string, widgetId: string) => {
      if (pageId === activePageId) {
        selectWidgetRef.current(widgetId);
      } else {
        pendingSelectRef.current = widgetId;
        setActivePageId(pageId);
      }
    },
    [activePageId, setActivePageId],
  );

  // Duplicate/Delete a widget from the layers-tree ⋮ menu. Same page → act now;
  // different page → switch first, then the page-change effect runs the action on
  // the now-active page (so the canvas's handlers operate on the correct list).
  const handleWidgetActionFromTree = useCallback(
    (pageId: string, widgetId: string, type: "duplicate" | "delete") => {
      if (pageId === activePageId) {
        widgetActionsRef.current[type](widgetId);
      } else {
        pendingWidgetActionRef.current = { type, widgetId };
        setActivePageId(pageId);
      }
    },
    [activePageId, setActivePageId],
  );

  // Connected integrations (Story 5.37 + 12.2). Keyed on the EFFECTIVE client id
  // (Story 37.8, blocker #10) so `connectedPlatforms` — the SINGLE connected-
  // decision source shared by the sample-data banner AND CanvasEditor's
  // live/mock merge — resolves on every entry path (incl. a saved report opened
  // without `?clientId=`). The two consumers can never disagree.
  // `connectedIntegrationsLoading` (review R2) gates the "unconnected" decision:
  // while the integrations query is in flight, `connectedPlatforms` defaults `[]`,
  // which would otherwise flash the sample banner + mock at a CONNECTED client
  // for a beat. We suppress the unconnected verdict until the query settles.
  const {
    integrations: clientIntegrations,
    connectedPlatforms: singleClientConnectedPlatforms,
    isLoading: connectedIntegrationsLoading,
  } = useClientIntegrations(effectiveClientId);

  // ── Member-union connected platforms for a roll-up (Story 40-6, AC5) ─────
  // The roll-up's connectedPlatforms = the UNION of its members' `platforms`.
  // Read from the shared ["clients"] cache (each Client carries `platforms`) and
  // intersect by member id — no per-member fetch. Idle for a single-client report.
  const { data: allClientsForUnion } = useQuery<ClientForUnion[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to load clients");
      const json = await res.json();
      return json.data as ClientForUnion[];
    },
    enabled: isRollup,
  });
  const rollupConnectedPlatforms = useMemo<string[]>(
    () => (isRollup ? unionMemberPlatforms(rollupMemberIds, allClientsForUnion ?? []) : []),
    [isRollup, rollupMemberIds, allClientsForUnion],
  );

  // The effective connected-platform set the canvas + banner read: the member
  // union for a roll-up, the single client's set otherwise.
  const connectedPlatforms = isRollup
    ? rollupConnectedPlatforms
    : singleClientConnectedPlatforms;

  // Group real pages by category (in PAGE_CATEGORIES order), mirroring the mockup's
  // FAKE_PAGES_BY_CATEGORY shape so the sidebar markup is wired 1:1.
  const sortedPages = useMemo(() => [...pages].sort((a, b) => a.order - b.order), [pages]);
  // Group pages into display-ready sections (show-iff filter + custom order).
  // The derivation lives in the tested `pagesByCategory` leaf (Story 37.9) so the
  // sidebar grouping is unit-covered without mounting the whole route. Always
  // shows the default section as a structural anchor so the sidebar never reads
  // as an empty void — gives the panel visible hierarchy even for a new report.
  // NOTE (code-review R1/R2): the leaf additionally coalesces a page with an
  // unknown/removed category into the default section instead of dropping it —
  // a deliberate genuine-bug fix over the prior inline behavior, not a pure
  // behavior-preserving move (the rest of the delegation is verbatim).
  const pagesByCategory = useMemo(
    () =>
      buildPagesByCategory(
        sortedPages,
        PAGE_CATEGORIES,
        DEFAULT_CATEGORY,
        sectionLabels,
        activeSections,
        sectionOrder,
      ),
    [sortedPages, sectionLabels, activeSections, sectionOrder],
  );

  // Page-reorder DnD (separate from widget DnD)
  const pageSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );
  const handlePageDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const fromIndex = sortedPages.findIndex((p) => p.id === active.id);
      const toIndex = sortedPages.findIndex((p) => p.id === over.id);
      if (fromIndex !== -1 && toIndex !== -1) reorderPages(fromIndex, toIndex);
    },
    [sortedPages, reorderPages]
  );
  const allPageIds = useMemo(() => sortedPages.map((p) => p.id), [sortedPages]);
  const isSinglePage = pages.length === 1;

  // Categories recommended for the client's connected integrations (highlighted in Add page).
  const recommendedCategories = useMemo(() => {
    const rec = new Set<string>();
    for (const platform of connectedPlatforms) {
      for (const cat of INTEGRATION_CATEGORY_MAP[platform] ?? []) rec.add(cat);
    }
    return rec;
  }, [connectedPlatforms]);

  // Date range
  const dateRange = useDateRange();

  // ── Saved-report hydration (Story 37.1, Tasks 1/2/3/5/6) ──────────────
  // Seeds the editable document ONCE from the loaded report when its query
  // resolves, in an effect (NOT during render) so the cross-hook setState
  // (resetPages / canvasZoom.setZoom / dateRange.hydrate) is committed cleanly.
  // Ref-guarded on the loaded report id so it fires exactly once per report —
  // never on refetch. After hydration, local state is authoritative.
  //
  // `hasHydratedRef` gates autosave so a slow/errored load or a corrupt layout
  // can never overwrite the real saved row with blank/template state (Task 6).
  // The effect can only run after the loading gate has let the editor render,
  // and `resetPages` zeroes `version`, so no autosave can fire before this.
  const loadedReportIdRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(!isExistingReport); // new reports need no hydrate
  // True when the loaded report's layoutJson failed to parse and the editor
  // degraded to a single empty page. Surfaced as a banner so the degradation is
  // NEVER silent — otherwise the next edit would autosave the empty page over the
  // original corrupt-but-possibly-recoverable blob with no warning (Story 37.1,
  // code-review finding).
  const [layoutCorruptNotice, setLayoutCorruptNotice] = useState(false);
  const loadedReportId = loadedReport?.id;
  useEffect(() => {
    if (!loadedReport) return;
    if (loadedReport.id === loadedReportIdRef.current) return;
    loadedReportIdRef.current = loadedReport.id;

    // Resolve all saved state through the SAME pure pipeline the round-trip test
    // exercises (buildReportHydration). The raw `layoutJson` string is parsed
    // defensively inside it; on corruption every layout-derived field degrades
    // to safe defaults WITHOUT wiping section state or crashing (AC #4).
    const hydration = buildReportHydration(loadedReport, (err) =>
      console.warn(
        `[report-editor] Corrupt layoutJson for report ${loadedReport.id}; falling back to a single empty page.`,
        err,
      ),
    );

    resetPages(
      hydration.pages,
      hydration.sectionLabels,
      hydration.activeSections,
      hydration.sectionOrder,
    );

    // Restore zoom (validated to [ZOOM_MIN, ZOOM_MAX] inside the pipeline; falls
    // back to the default when the layout carried no valid saved zoom).
    // `hasInitialZoom` mirrors the template path's flag for parity (true iff a
    // real saved zoom was applied).
    canvasZoom.setZoom(hydration.zoomLevel);
    hasInitialZoom.current = hydration.hasSavedZoom;

    // Saved name wins over the template+client default.
    if (hydration.name) setReportName(hydration.name);

    // Format: the top-level column is AUTHORITATIVE (C3). layoutJson.format ignored.
    setSelectedFormat(hydration.format);

    // Date range + comparison dates restored verbatim (Task 3). Only present
    // when both primary ends were saved; comparison restored exactly as saved
    // (no recompute) by useDateRange.hydrate.
    if (hydration.dateRange) {
      dateRange.hydrate(hydration.dateRange);
    }

    // Surface (or clear) the corruption banner for THIS report — always assign so
    // navigating from a corrupt report to a clean one without remount clears it.
    setLayoutCorruptNotice(hydration.layoutCorrupt);

    // Hydration complete → autosave may now run for this existing report.
    hasHydratedRef.current = true;
    // Intentionally keyed on the loaded report id ONLY (hydrate-once-per-report).
    // resetPages/canvasZoom/dateRange/setReportName/setSelectedFormat identities
    // are stable, and `loadedReport` is read fresh via the closure each time the
    // id changes — adding them as deps would re-run the one-shot hydration.
    // (react-hooks/exhaustive-deps is not enabled in this project's eslint config.)
  }, [loadedReportId]);

  // Dirty state — the report is dirty when the edit `version` counter has moved
  // PAST the version that was last successfully saved. (Previously `version > 0`,
  // which stayed true forever after the first edit even though the save succeeded
  // — so the exit guard wrongly reported "unsaved changes" right after a save.)
  const [lastSavedVersion, setLastSavedVersion] = useState(0);
  const isDirty = version > lastSavedVersion;
  // `versionRef` lets performSave (which reads from refs) capture the version at
  // save time without re-creating the callback on every edit.
  const versionRef = useRef(version);
  versionRef.current = version;

  // Auto-save (debounced on version counter)
  const debouncedVersion = useDebounce(version, 2000);
  const savedReportIdRef = useRef<string | null>(reportId !== "new" ? reportId : null);

  // Mutable refs for values used in save — avoids stale closures (H3 fix)
  const pagesRef = useRef(pages);
  pagesRef.current = pages;

  const clientRef = useRef(client);
  clientRef.current = client;

  const sectionLabelsRef = useRef(sectionLabels);
  sectionLabelsRef.current = sectionLabels;
  const activeSectionsRef = useRef(activeSections);
  activeSectionsRef.current = activeSections;
  const sectionOrderRef = useRef(sectionOrder);
  sectionOrderRef.current = sectionOrder;

  // Story 40-6 — the roll-up identity threaded into the save context so the
  // CREATE payload carries kind/scope/members (no clientId) for a roll-up.
  const rollupSaveShape = isRollup
    ? {
        isRollup: true as const,
        rollupScope,
        brandId: rollupBrandId,
        memberClientIds: rollupMemberIds,
      }
    : { isRollup: false as const, rollupScope: null, brandId: null, memberClientIds: [] as string[] };

  const saveContextRef = useRef({
    reportName,
    // Use the EFFECTIVE client id (search `?clientId=` OR the saved report's own
    // client) — a saved report opened without the search param still has a client,
    // so Cmd+S must not bail on an empty raw `clientId` (it did, silently).
    clientId: effectiveClientId,
    templateId,
    selectedFormat,
    dateRange: dateRange.state,
    grid: selectedTemplate?.parsedLayout.grid ?? { columns: 12 },
    zoomLevel: canvasZoom.displayZoom,
    rollup: rollupSaveShape,
  });
  saveContextRef.current = {
    reportName,
    clientId: effectiveClientId,
    templateId,
    selectedFormat,
    dateRange: dateRange.state,
    grid: selectedTemplate?.parsedLayout.grid ?? { columns: 12 },
    zoomLevel: canvasZoom.displayZoom,
    rollup: rollupSaveShape,
  };

  // AbortController for concurrent save prevention (M4 fix)
  const saveAbortRef = useRef<AbortController | null>(null);

  const performSave = useCallback(async () => {
    saveAbortRef.current?.abort();
    const controller = new AbortController();
    saveAbortRef.current = controller;

    const pagesToSave = pagesRef.current;
    const ctx = saveContextRef.current;
    // A single-client save needs a clientId; a ROLL-UP save needs ≥1 member
    // (Story 40-6). Bail if neither identity is ready (e.g. before a brand's
    // members resolve) so we never POST a half-formed report.
    if (ctx.rollup.isRollup) {
      if (ctx.rollup.memberClientIds.length === 0) return;
    } else if (!ctx.clientId) {
      return;
    }

    // Snapshot the edit version being persisted, so on success we can mark the
    // editor clean up to THIS version (edits made during the save bump it higher
    // and keep it dirty → another autosave).
    const versionAtSave = versionRef.current;

    setSaveStatus("saving");
    try {
      const layoutJson = JSON.stringify({
        version: 1,
        grid: ctx.grid,
        pages: pagesToSave.map((p) => ({
          id: p.id,
          title: p.title,
          order: p.order,
          ...(p.category ? { category: p.category } : {}),
          widgets: p.widgets.map((w) => ({
            id: w.id,
            type: w.type,
            title: w.title,
            position: w.position,
            config: w.config,
            ...(w.autoSize != null ? { autoSize: w.autoSize } : {}),
          })),
        })),
        format: ctx.selectedFormat,
        zoomLevel: ctx.zoomLevel,
        sectionLabels: sectionLabelsRef.current,
        activeSections: activeSectionsRef.current,
        sectionOrder: sectionOrderRef.current,
      });

      const existingId = savedReportIdRef.current;
      // Persist comparison dates (Story 37.1, Task 3 — CLIENT-ONLY; the server
      // schema already accepts `comparison` as `.optional()`). Include the key
      // ONLY when comparison is enabled AND both ends are set; omit it otherwise
      // so the server clears the columns (comparison?.start ?? null).
      const cmp = ctx.dateRange;
      const comparison =
        cmp.comparisonEnabled && cmp.comparisonStart && cmp.comparisonEnd
          ? { start: cmp.comparisonStart, end: cmp.comparisonEnd }
          : undefined;
      // Common fields shared by both kinds (and both create + update). A roll-up
      // carries NO `_clientContext` (no single client) and no clientId.
      const common = {
        name: ctx.reportName,
        dateRange: { start: ctx.dateRange.start, end: ctx.dateRange.end },
        ...(comparison ? { comparison } : {}),
        layoutJson,
        dataSnapshotJson: ctx.rollup.isRollup
          ? JSON.stringify({})
          : JSON.stringify({
              _clientContext: {
                industry: clientRef.current?.industry ?? null,
                businessGoals: clientRef.current?.businessGoals ?? null,
                notes: clientRef.current?.notes ?? null,
              },
            }),
        format: ctx.selectedFormat,
      };

      let res: Response;
      if (existingId) {
        // UPDATE — the PUT handler only mutates name/dates/layout/snapshot/format;
        // kind/scope/members are immutable post-create. Send NO clientId/kind for a
        // roll-up (the shared schema rejects a null clientId; absent is fine).
        const updatePayload = ctx.rollup.isRollup
          ? common
          : { ...common, clientId: ctx.clientId, templateId: ctx.templateId };
        res = await apiPut(`/api/reports/${existingId}`, updatePayload);
      } else {
        // CREATE — a roll-up POSTs kind/scope + the member set (no clientId); the
        // server discriminator (40-6 Task 1) validates the shape. Single-client is
        // byte-identical to today.
        const createPayload = ctx.rollup.isRollup
          ? {
              ...common,
              kind: "rollup" as const,
              ...(ctx.rollup.rollupScope ? { rollupScope: ctx.rollup.rollupScope } : {}),
              ...(ctx.rollup.rollupScope === "brand"
                ? { brandId: ctx.rollup.brandId ?? undefined }
                : { memberClientIds: ctx.rollup.memberClientIds }),
            }
          : { ...common, clientId: ctx.clientId, templateId: ctx.templateId };
        res = await apiPost("/api/reports", createPayload);
      }

      if (controller.signal.aborted) return;

      if (res.ok) {
        const json = (await res.json().catch(() => null)) as { data?: { id?: string } } | null;
        if (json?.data?.id && !savedReportIdRef.current) {
          savedReportIdRef.current = json.data.id;
          // After the first save the report exists server-side. A SAVED roll-up
          // re-loads its kind/members from GET /api/reports/:id, so the search
          // need only carry `format` (no clientId/kind/members). Single-client
          // keeps its existing search shape.
          navigate({
            to: "/reports/$reportId/edit",
            params: { reportId: json.data.id },
            search: ctx.rollup.isRollup
              ? { format: ctx.selectedFormat }
              : { templateId: ctx.templateId, clientId: ctx.clientId, format: ctx.selectedFormat },
            replace: true,
          });
        }
        // Keep the cached report in sync with what we just persisted, so leaving
        // and re-entering hydrates from the SAVED layout instead of a stale cache
        // (the hydration effect locks onto the first `loadedReport` it sees per id).
        const savedId = existingId ?? savedReportIdRef.current;
        if (savedId) {
          queryClient.setQueryData<LoadedReport>(["report", savedId], (old) =>
            old
              ? {
                  ...old,
                  name: ctx.reportName,
                  layoutJson,
                  format: ctx.selectedFormat,
                  dateRangeStart: ctx.dateRange.start,
                  dateRangeEnd: ctx.dateRange.end,
                }
              : old,
          );
        }
        // Mark clean up to the version we just persisted.
        setLastSavedVersion(versionAtSave);
        setSaveStatus("saved");
      } else {
        setSaveStatus("unsaved");
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      setSaveStatus("unsaved");
    }
  }, [navigate, queryClient]);

  // Auto-save when debounced version changes.
  // SAVE-CLOBBER GUARD (Story 37.1, Task 6): for an existing report, never save
  // until hydration has completed. If the load query is still in-flight, errored,
  // or the layout was corrupt, an early autosave would PUT blank/template state
  // over the real saved row — re-introducing the very data loss this story fixes.
  // (`reset()` zeroing `version` already prevents a dirty flag on hydrate; this
  // ref makes the guarantee explicit and testable.)
  // Story 40-6: a roll-up has no effectiveClientId — it's "save-ready" once its
  // member set resolves (≥1 member). Gate autosave on EITHER a client OR a
  // resolved roll-up member set; performSave re-checks the same condition.
  const saveIdentityReady = isRollup ? rollupMemberIds.length > 0 : !!effectiveClientId;
  useEffect(() => {
    if (!isDirty || !saveIdentityReady) return;
    if (debouncedVersion === 0) return;
    if (!hasHydratedRef.current) return;
    performSave();
  }, [debouncedVersion, isDirty, saveIdentityReady, performSave]);

  // Mark unsaved when widgets change
  useEffect(() => {
    if (isDirty) setSaveStatus("unsaved");
  }, [isDirty]);

  // ── Navigation & exit guard ────────────────────────────────────────

  const handleClose = useCallback(() => {
    if (isDirty) setShowExitDialog(true);
    else navigate({ to: "/reports" });
  }, [isDirty, navigate]);

  const handleConfirmExit = useCallback(() => {
    setShowExitDialog(false);
    navigate({ to: "/reports" });
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      setShowExitDialog(true);
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty]);

  // Ctrl/Cmd+S — immediate save
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        performSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [performSave]);

  // Zoom keyboard shortcuts (Story 5.38, AC #6)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === "=" || e.key === "+") {
        e.preventDefault();
        canvasZoom.zoomIn();
        return;
      }
      if (e.key === "-") {
        e.preventDefault();
        canvasZoom.zoomOut();
        return;
      }
      if (e.key === "0") {
        e.preventDefault();
        computeFitToWidth();
        return;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [canvasZoom, computeFitToWidth]);

  // Trackpad pinch / Ctrl+wheel zoom toward the cursor. Unified transform model:
  // zoomAtPoint mutates pan so the content point under the cursor stays fixed,
  // and canvasZoom.setZoom commits the new scale. Both are React state driving
  // the single translate()scale() transform — no scroll math, no drift.
  useEffect(() => {
    const handler = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      const oldZoom = canvasZoom.zoomLevel;
      const delta = Math.max(-50, Math.min(50, -e.deltaY));
      const sensitivity = 1.5;
      const newZoom = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, oldZoom + delta * sensitivity));
      if (Math.abs(newZoom - oldZoom) < 0.01) return;

      canvasPan.zoomAtPoint(oldZoom / 100, newZoom / 100, e.clientX, e.clientY);
      canvasZoom.setZoom(newZoom);
    };

    window.addEventListener("wheel", handler, { passive: false });
    return () => window.removeEventListener("wheel", handler);
  }, [canvasZoom, canvasPan]);

  const handlePreview = useCallback(() => {
    navigate({
      to: "/reports/generate",
      search: { clientId, templateId, format: selectedFormat },
    });
  }, [navigate, clientId, templateId, selectedFormat]);

  const handleWidgetUpdate = useCallback(
    (widgetId: string, changes: Partial<WidgetConfig>) => {
      updateActivePageWidgets((prev) =>
        prev.map((w) => {
          if (w.id !== widgetId) return w;
          const updated = { ...w, ...changes };
          if (changes.position) updated.position = { ...w.position, ...changes.position };
          return updated;
        })
      );
    },
    [updateActivePageWidgets]
  );

  // Story 37.3: add a widget to the active page from a floating category panel,
  // carrying an optional initial config so the configure-then-add panels
  // (Images/Embeds) drop a fully-populated widget — never a blank one (AC #4).
  // Placement reuses the SAME shared gridUtils helpers CanvasEditor's own
  // handleWidgetAdd uses (calculateGridPlacement + getNextAvailablePosition +
  // getSmartDefaults) — not a copy of its internals (37.7 territory untouched).
  //
  // Returns whether the widget was actually added. The panels reset their draft
  // ONLY on a true result so a rejected add (page full, or a duplicate cover
  // page) never wipes the user's just-configured upload/URL (code-review R1).
  // The guards mirror CanvasEditor.handleWidgetAdd so this parallel add path
  // can't drift if a future panel (37.4/37.5) is wired through the same
  // `onWidgetAdd` — at-cap and one-cover-page-max are enforced here too.
  const handleAddWidget = useCallback(
    (type: WidgetType, initialConfig?: Record<string, unknown>): boolean => {
      // All guard + placement rules live in the pure `planConfiguredWidgetAdd`
      // (unit-tested in gridUtils.test.ts; build-to-sell / code-review R3). We
      // plan against the ref snapshot — the committed state at click time, read
      // without a deps entry so this callback stays stable across widget edits
      // (code-review R2). A `null` plan means the add was rejected (page full /
      // duplicate cover page); the panels reset their draft ONLY on a `true`
      // return, so a rejected add never wipes the user's upload/URL (R1). The
      // committed widget is plumbed through the async setter so the same plan
      // becomes the data model.
      const { colSpan, rowSpan } = getSmartDefaults(type as SmartDefaultWidgetType);
      const newWidget = planConfiguredWidgetAdd({
        widgets: activePageWidgetsRef.current,
        type,
        id: crypto.randomUUID(),
        title: nextWidgetTitle(type, activePageWidgetsRef.current),
        colSpan,
        rowSpan,
        config: initialConfig ?? {},
      });
      if (!newWidget) return false;
      updateActivePageWidgets((prev) => [...prev, newWidget]);
      return true;
    },
    [updateActivePageWidgets]
  );

  // Story 18.4 AC #9 — atomic multi-widget add for the combined budget/goal
  // dashboard preset. Looping `handleAddWidget` is unsafe: each call plans
  // against `activePageWidgetsRef.current`, which only refreshes on the NEXT
  // render, so N sequential adds collide on position. `planPresetWidgetsAdd`
  // positions the whole batch against ONE working snapshot, and the append
  // commits in a single functional update (the CanvasEditor.handlePresetAdd
  // precedent). All-or-nothing — returns false (adds nothing) when the page
  // lacks slots for the full batch.
  const handleAddPresetWidgets = useCallback(
    (presetWidgets: PresetWidget[]): boolean => {
      const positioned = planPresetWidgetsAdd({
        widgets: activePageWidgetsRef.current,
        presetWidgets,
      });
      if (!positioned) return false;
      updateActivePageWidgets((prev) => [...prev, ...positioned]);
      return true;
    },
    [updateActivePageWidgets]
  );

  // ── Loading state ──────────────────────────────────────────────────
  // Wait on the saved-report query for an existing report too (Story 37.1) —
  // rendering the canvas before hydration would flash a blank/template state and
  // risk an early autosave clobber. The autosave guard (hasHydratedRef) is the
  // hard safety net; this gate is the UX layer.
  if (templatesLoading || (isExistingReport && reportLoading)) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-6">
          <div className="w-14 h-14 border-[6px] border-muted border-t-primary rounded-full animate-spin" />
          <p className="text-base font-medium text-muted-foreground animate-pulse">
            Preparing your report editor...
          </p>
        </div>
      </div>
    );
  }

  // ── Load-error state (Story 37.1) ──────────────────────────────────
  // A 404 / load failure for an existing report must NOT fall through to a blank
  // editable canvas — that canvas would autosave-clobber the real saved row. The
  // autosave guard (hasHydratedRef stays false on error) already blocks the
  // write; this screen makes the failure visible and offers a safe exit.
  if (isExistingReport && reportLoadError) {
    return (
      <div
        data-testid="report-editor-load-error"
        className="fixed inset-0 z-50 flex items-center justify-center bg-background"
      >
        <div className="flex flex-col items-center gap-4 text-center max-w-md px-6">
          <h2 className="text-xl font-semibold text-foreground">
            Couldn’t load this report
          </h2>
          <p className="text-sm text-muted-foreground">
            It may have been deleted, or you may not have access to it. Your
            existing report has not been changed.
          </p>
          <Button
            variant="outline"
            className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => navigate({ to: "/reports" })}
          >
            Back to Reports
          </Button>
        </div>
      </div>
    );
  }

  // The FloatingWidgetSettings panel occupies the SAME right-hand slot as the
  // category picker and is the SAME width (PICKER_PANEL_WIDTH), so when a widget
  // is selected the canvas paper + notch strip must shift left exactly as they do
  // for the integrations picker — otherwise the editor would overlap the report.
  // Take the max so whichever panel is open drives the push (in practice only one
  // is open at a time).
  const widgetEditorOpen = selectedWidget !== null;
  const effectivePaperLeftShift = Math.max(
    getPaperLeftShift(activeCategory),
    widgetEditorOpen ? paperShiftForWidth(PICKER_PANEL_WIDTH) : 0,
  );
  const effectiveNotchMarginRight = Math.max(
    getNotchMarginRight(activeCategory),
    widgetEditorOpen ? notchMarginForWidth(PICKER_PANEL_WIDTH) : 0,
  );

  return (
    <>
      {/* Corruption notice (Story 37.1): the saved layout could not be parsed and
          the canvas was rebuilt empty. Surfaced so the degradation is never silent
          before the user's next edit re-persists the empty canvas. Non-blocking —
          AC #4 requires the user can still edit and re-save. */}
      {layoutCorruptNotice && (
        <div
          data-testid="report-editor-layout-corrupt-banner"
          role="alert"
          className="fixed top-0 inset-x-0 z-[60] flex items-center justify-center gap-3 bg-amber-50 text-amber-900 px-4 py-2 text-[13px]"
          style={{ borderBottom: "0.5px solid var(--hairline)" }}
        >
          <span>
            This report’s saved layout couldn’t be read, so the canvas was rebuilt
            empty. Editing and saving will replace the unreadable version.
          </span>
          <button
            type="button"
            className="shrink-0 underline cursor-pointer"
            onClick={() => setLayoutCorruptNotice(false)}
          >
            Dismiss
          </button>
        </div>
      )}
      <div
        data-testid="report-editor-layout"
        className="fixed inset-0 z-50 flex overflow-hidden bg-background"
      >
        {/* ── Left sidebar: Pages ───────────────────────────────────── */}
        <motion.aside
          data-testid="editor-left-sidebar"
          className="shrink-0 flex flex-col bg-background overflow-hidden"
          animate={{ width: leftWidth }}
          initial={false}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Report status — save state icon stays visible even when collapsed */}
          <div className="shrink-0 px-4 pt-6 pb-2" data-testid="editor-report-status">
            <div className="flex items-center justify-center gap-1.5" title={
              saveStatus === "unsaved" ? "Unsaved changes" : saveStatus === "saving" ? "Saving…" : "All changes saved"
            }>
              {saveStatus === "saving" ? (
                <CircleNotch size={16} weight="bold" className="shrink-0 text-foreground animate-spin" />
              ) : saveStatus === "unsaved" ? (
                <CloudArrowUp size={16} weight="fill" className="shrink-0 text-amber-500" />
              ) : (
                <CheckCircle size={16} weight="fill" className="shrink-0 text-green-500" />
              )}
              {/* Label hides when collapsed; the icon stays */}
              <span
                className="text-[13.5px] text-foreground whitespace-nowrap overflow-hidden"
                data-testid="editor-save-status"
                style={{
                  maxWidth: leftCollapsed ? 0 : 200,
                  opacity: leftCollapsed ? 0 : 1,
                  transition: "max-width 0.25s ease, opacity 0.2s ease",
                }}
              >
                {saveStatus === "unsaved"
                  ? "Unsaved changes"
                  : saveStatus === "saving"
                  ? "Saving…"
                  : "All changes saved"}
              </span>
              <span className="hidden" data-testid="editor-presence" aria-label="People editing" />
            </div>
            {/* Divider under the status — hidden when collapsed */}
            {!leftCollapsed && (
              <div className="mt-5 mx-4" style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.5)" }} />
            )}
          </div>

          {/* Pages list — EXACT mockup markup (via EditorPageRow), wired to real pages + DnD */}
          <nav className="flex-1 px-4 overflow-y-auto pt-2">
            <DndContext sensors={pageSensors} collisionDetection={closestCenter} onDragEnd={handlePageDragEnd}>
              <SortableContext items={allPageIds} strategy={verticalListSortingStrategy}>
                {(() => {
                  let globalIndex = 0;
                  return pagesByCategory.map((section, idx) => (
                    <div key={section.categoryId}>
                      <div className="mb-4">
                        <div
                          className="overflow-hidden"
                          style={{
                            height: leftCollapsed ? 0 : "auto",
                            opacity: leftCollapsed ? 0 : 1,
                            transition: "opacity 0.3s ease",
                          }}
                        >
                          <SectionHeaderEdit
                            label={section.category}
                            onSave={(label) => setSectionLabel(section.categoryId, label)}
                            onDelete={() => deleteSection(section.categoryId)}
                            canDelete={section.pages.length === 0}
                            onMoveUp={() =>
                              moveSection(
                                section.categoryId,
                                "up",
                                pagesByCategory.map((s) => s.categoryId)
                              )
                            }
                            onMoveDown={() =>
                              moveSection(
                                section.categoryId,
                                "down",
                                pagesByCategory.map((s) => s.categoryId)
                              )
                            }
                            canMoveUp={idx > 0}
                            canMoveDown={idx < pagesByCategory.length - 1}
                          />
                        </div>
                        <ul className="space-y-1">
                          {section.pages.map((page) => {
                            globalIndex += 1;
                            return (
                              <EditorPageRow
                                key={page.id}
                                page={page}
                                pageNumber={globalIndex}
                                isActive={page.id === activePageId}
                                isSinglePage={isSinglePage}
                                leftCollapsed={leftCollapsed}
                                onSelect={() => setActivePageId(page.id)}
                                onRename={(title) => renamePage(page.id, title)}
                                onDuplicate={() => duplicatePage(page.id)}
                                onDelete={() => deletePage(page.id)}
                                onChangeCategory={(cat) => changePageCategory(page.id, cat)}
                                selectedWidgetId={selectedWidget?.id ?? null}
                                onSelectWidget={handleSelectWidgetFromTree}
                                onWidgetAction={handleWidgetActionFromTree}
                              />
                            );
                          })}
                          {section.pages.length === 0 && !leftCollapsed && (
                            <li>
                              <button
                                type="button"
                                onClick={() => addPage(section.categoryId)}
                                className="flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-[12px] italic text-foreground/40 hover:text-foreground/70 hover:bg-background border-[0.5px] border-dashed border-transparent hover:border-input transition-colors cursor-pointer"
                                data-testid={`section-empty-${section.categoryId}`}
                              >
                                <Plus size={13} className="shrink-0" />
                                Add a page
                              </button>
                            </li>
                          )}
                        </ul>
                      </div>
                      {idx < pagesByCategory.length - 1 && (
                        <div
                          className="mx-2 mb-4"
                          style={{ borderBottom: "0.5px solid hsl(var(--input) / 0.5)" }}
                        />
                      )}
                    </div>
                  ));
                })()}
              </SortableContext>
            </DndContext>

            {/* Add page (direct) + Add section (category picker), split by a divider */}
            {!leftCollapsed && (
              <div className="mt-1 flex items-center gap-1">
                {/* Add page — creates a new page directly with the default section */}
                <button
                  type="button"
                  data-testid="add-page-button"
                  aria-label="Add page"
                  title="Add page"
                  onClick={() => addPage()}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 px-2 text-foreground hover:text-foreground hover:bg-background hover:shadow-xs border-[0.5px] border-transparent hover:border-input transition-colors cursor-pointer"
                >
                  <Plus size={16} className="shrink-0" />
                  <span className="text-[13.5px]">Page</span>
                </button>

                {/* Vertical divider — matches the section-separator rule (var(--input) / 0.5) */}
                <span
                  aria-hidden="true"
                  className="shrink-0 self-stretch my-1"
                  style={{ width: "0.5px", background: "hsl(var(--input) / 0.5)" }}
                />

                {/* Add section — opens a category picker so a new page starts a new section */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="add-section-button"
                      aria-label="Add section"
                      title="Add section"
                      className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 px-2 text-foreground hover:text-foreground hover:bg-background hover:shadow-xs border-[0.5px] border-transparent hover:border-input transition-colors cursor-pointer"
                    >
                      <Plus size={16} className="shrink-0" />
                      <span className="text-[13.5px]">Section</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    sideOffset={6}
                    className="rounded-xl p-2 shadow-md w-48 [&>div:first-child]:right-auto [&>div:first-child]:left-4"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  >
                    {ALL_PAGE_CATEGORIES.map((cat) => {
                      const CatIcon = CATEGORY_ICON_MAP[cat.iconName] ?? SquaresFour;
                      const isRecommended = recommendedCategories.has(cat.id);
                      return (
                        <DropdownMenuItem
                          key={cat.id}
                          onClick={() => addSection(cat.id)}
                          className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                          data-testid={`add-section-category-${cat.id}`}
                        >
                          <CatIcon size={14} className="mr-1.5" />
                          {cat.label}
                          {isRecommended && (
                            <span className="ml-auto text-[11px] text-muted-foreground">(Recommended)</span>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </nav>

          {/* Footer: Connected Sources + collapse toggle */}
          <div className="shrink-0 px-3 pb-3 space-y-5">
            {/* Connected Sources — label hides when collapsed; the logo stack stays */}
            <button
              type="button"
              onClick={() => effectiveClientId && navigate({ to: "/clients/$clientId", params: { clientId: effectiveClientId } })}
              disabled={!effectiveClientId}
              title="Manage connected sources"
              className="w-full px-1 cursor-pointer disabled:cursor-default"
              data-testid="editor-connected-sources"
            >
              {!leftCollapsed && (
                <div className="text-[13.5px] font-medium text-foreground mb-2 text-center">
                  Connected Sources
                </div>
              )}
              {connectedPlatforms.length > 0 ? (
                <div className="flex items-center justify-center flex-wrap gap-2">
                  {connectedPlatforms.map((platformId) => {
                    const platform = PICKER_PLATFORMS.find((p) => p.platform === platformId);
                    if (!platform?.domain) return null;
                    return (
                      <img
                        key={platformId}
                        src={logoUrl(platform.domain)}
                        alt={platform.displayName}
                        title={platform.displayName}
                        loading="lazy"
                        className="w-5 h-5 shrink-0 rounded-sm object-contain"
                      />
                    );
                  })}
                </div>
              ) : (
                !leftCollapsed && (
                  <div className="text-[13.5px] italic text-foreground/40 text-center">No sources connected</div>
                )
              )}
            </button>

            <button
              type="button"
              onClick={() => setLeftCollapsed((v) => !v)}
              className="flex items-center justify-center w-full rounded-lg text-foreground bg-accent/50 hover:bg-accent/50 shadow-sm transition-colors cursor-pointer"
              style={{ border: "0.25px solid var(--hairline)" }}
              title={leftCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="page-sidebar-collapse"
            >
              <motion.div
                className="flex items-center justify-center py-3 cursor-pointer"
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                {leftCollapsed ? (
                  <ArrowLineRight size={18} className="shrink-0 text-foreground" />
                ) : (
                  <ArrowLineLeft size={18} className="shrink-0 text-foreground" />
                )}
                {!leftCollapsed && (
                  <span className="whitespace-nowrap text-[13.5px] font-medium ml-[7px]">
                    Collapse
                  </span>
                )}
              </motion.div>
            </button>
          </div>
        </motion.aside>

        {/* ── Main editor column ───────────────────────────────────── */}
        <div className="relative flex-1 flex flex-col overflow-hidden">
          {/* dnd-kit context wrapping the canvas (drop target) + the floating
              palette (drag source). Non-DOM wrapper — layout/pan-zoom unaffected.
              Epic 37 canvas migration off react-grid-layout. */}
          <ReportDndProvider scale={canvasZoom.zoomLevel / 100} format={selectedFormat}>
          {/* Top thin strip with notches — shrinks from the right when any
              floating category picker is open, so the notches recenter over the
              pushed canvas (Story 37.2: was integrations-only) */}
          <div
            className="relative shrink-0 bg-background h-[21px] overflow-visible"
            style={{
              // Story 37.2: width-derived from the active floating panel (no
              // magic 282) — 282 for a 250-wide category, 0 when not floating.
              marginRight: effectiveNotchMarginRight,
              transition: "margin-right 0.25s ease",
            }}
          >
            {/* Actions notch — left: Sources + Schedule */}
            <div className="absolute z-20 left-6 top-0 h-[48px]">
              <div className="relative h-full flex items-center gap-2 px-3 bg-background rounded-b-[20px]">
                <EditorActionsRight
                  dateRangeState={dateRange.state}
                  onSelectPreset={dateRange.selectPreset}
                  onSelectCustomRange={dateRange.selectCustomRange}
                  onToggleComparison={dateRange.toggleComparison}
                  onSetComparisonMode={dateRange.setComparisonMode}
                  connectedIntegrations={connectedPlatforms}
                  clientId={effectiveClientId}
                />
                <div
                  className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                  style={{
                    clipPath: "inset(21px -10px -10px -10px)",
                    border: "0.5px solid var(--hairline)",
                    borderTop: "none",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  }}
                />
              </div>
            </div>

            {/* Title notch — center: the report title */}
            <div className="absolute z-20 left-1/2 -translate-x-1/2 top-0 h-[48px]">
              <div
                className="relative h-full flex items-center justify-center px-4 bg-background rounded-b-[20px]"
              >
                <EditorReportTitle reportName={reportName} onReportNameChange={setReportName} />
                <div
                  className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                  style={{
                    clipPath: "inset(21px -10px -10px -10px)",
                    border: "0.5px solid var(--hairline)",
                    borderTop: "none",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  }}
                />
              </div>
            </div>

            {/* Actions notch — right: Search (magnifying glass) + Preview / Share + Close */}
            <div className="absolute z-20 right-6 top-0 h-[48px]">
              <div className="relative h-full flex items-center gap-2 px-3 bg-background rounded-b-[20px]">
                <EditorSearchTrigger />
                <EditorActionsLeft onPreview={handlePreview} onClose={handleClose} closeLast />
                <div
                  className="absolute inset-0 rounded-b-[20px] pointer-events-none"
                  style={{
                    clipPath: "inset(21px -10px -10px -10px)",
                    border: "0.5px solid var(--hairline)",
                    borderTop: "none",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Canvas main area — dot-grid recessed background, real CanvasEditor inside */}
          <main
            ref={canvasPan.ref as React.Ref<HTMLElement>}
            onMouseDown={canvasPan.onMouseDown}
            data-testid="editor-center-canvas"
            // `relative` so CanvasEditor's canvas-level data affordances
            // (Story 37.8 updating/stale/error indicators) anchor to the canvas
            // area, not pan/zoom with the transformed content.
            className="relative flex-1 overflow-hidden rounded-tl-[2rem] rounded-tr-[2rem] cursor-grab"
            style={{
              // Content is positioned by a translate()scale() transform (not
              // scroll), so the container clips (overflow-hidden) and pan/zoom
              // move the transform. No scroll padding needed.
              backgroundColor: "var(--app-surface)",
              backgroundImage:
                "radial-gradient(circle, hsl(214 25% 65% / 0.5) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              boxShadow: "inset 1px 1px 3px -1px rgba(0,0,0,0.05)",
              borderLeft: "0.5px solid var(--hairline)",
              borderRight: "0.5px solid var(--hairline)",
              borderTop: "0.5px solid var(--hairline)",
            }}
          >
            {/* Sample data banner — when the (effective) client has no connected
                integrations. Gated on the same connected verdict as the canvas
                live/mock merge (`effectiveClientId && !isClientConnected(...)`,
                Story 37.8 blocker #10) — NO `&& client` guard, which previously
                created a transient "mock shown with no banner" hole while the
                client-name query was still loading (review R1). The client name
                degrades gracefully to "this client" until `client` resolves.
                Suppressed while `connectedIntegrationsLoading` so a CONNECTED
                client isn't flashed the "disconnected" banner before the
                integrations query settles (review R2). */}
            {effectiveClientId && !connectedIntegrationsLoading && !isClientConnected(effectiveClientId, connectedPlatforms) && (
              <div
                className="mx-auto mb-4 max-w-2xl flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-background shadow-xs"
                style={{ border: "0.5px solid var(--hairline)" }}
                data-testid="sample-data-banner"
              >
                <span className="text-[13px] text-foreground flex-1">
                  Showing <span className="font-semibold">sample data</span>. Connect integrations to
                  see real numbers for {client?.name ?? "this client"}.
                </span>
                <button
                  type="button"
                  onClick={() => navigate({ to: "/clients/$clientId", params: { clientId: effectiveClientId } })}
                  className="shrink-0 px-3 py-1.5 rounded-2xl text-[13px] font-medium text-white bg-primary cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all"
                >
                  Connect
                </button>
              </div>
            )}
            {/* Roll-up sample banner (Story 40-6) — a roll-up has no single
                client; it shows sample data when NO member has any connected
                platform in the union. No "Connect" CTA (members are many). */}
            {isRollup && rollupMemberIds.length > 0 && connectedPlatforms.length === 0 && (
              <div
                className="mx-auto mb-4 max-w-2xl flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-background shadow-xs"
                style={{ border: "0.5px solid var(--hairline)" }}
                data-testid="rollup-sample-data-banner"
              >
                <span className="text-[13px] text-foreground flex-1">
                  Showing <span className="font-semibold">sample data</span>. Connect integrations on
                  the member clients to see aggregated numbers.
                </span>
              </div>
            )}
            <CanvasEditor
              widgets={activePageWidgets as WidgetConfig[]}
              onWidgetsChange={updateActivePageWidgets as CanvasEditorProps["onWidgetsChange"]}
              onWidgetsSilentChange={updateActivePageWidgetsSilent as CanvasEditorProps["onWidgetsSilentChange"]}
              onUndo={undo}
              onRedo={redo}
              initialWidgets={(initialPages[0]?.widgets as WidgetConfig[]) ?? []}
              comparisonEnabled={dateRange.state.comparisonEnabled}
              reportDateRangeStart={dateRange.state.start}
              reportDateRangeEnd={dateRange.state.end}
              // Story 37.8: comparison dates for the live populate (the hook
              // only fires the comparison query when both are set + enabled).
              comparisonStart={dateRange.state.comparisonStart}
              comparisonEnd={dateRange.state.comparisonEnd}
              selectedClientName={client?.name}
              selectedClientLogoUrl={client?.logoUrl ?? undefined}
              // Story 37.8: the EFFECTIVE client id (blocker #9) + connected
              // platforms (blocker #10) drive the canvas live/mock merge.
              clientId={effectiveClientId}
              connectedPlatforms={connectedPlatforms}
              // Story 40-6: a roll-up routes live data through useRollupData over
              // the member set + the member-union connectedPlatforms above; the
              // single-client path is unchanged.
              reportKind={isRollup ? "rollup" : "client"}
              memberClientIds={isRollup ? rollupMemberIds : undefined}
              format={selectedFormat}
              onSelectedWidgetChange={handleSelectedWidgetChange}
              registerDeselect={(fn) => { deselectWidgetRef.current = fn; }}
              registerSelect={(fn) => { selectWidgetRef.current = fn; }}
              registerWidgetActions={(fns) => { widgetActionsRef.current = fns; }}
              zoomLevel={canvasZoom.zoomLevel}
              // Story 37.2: width-derived from the active floating panel (no
              // magic 140) — 140 for a 250-wide category, 0 when not floating.
              paperLeftShift={effectivePaperLeftShift}
              panX={canvasPan.pan.x}
              panY={canvasPan.pan.y}
            />
          </main>

          {/* Floating category picker — overlays the canvas dot-grid, anchored
              right. Story 37.2: ALL 8 sidebar categories now float through this
              one registry-driven component (was integrations-only). */}
          <FloatingCategoryPicker
            activeCategory={activeCategory}
            selectedIntegration={selectedIntegration}
            setSelectedIntegration={setSelectedIntegration}
            clientIntegrations={clientIntegrations}
            onWidgetAdd={handleAddWidget}
            clientId={effectiveClientId}
            onPresetWidgetsAdd={handleAddPresetWidgets}
            // Story 40-6 (AC6): the aggregate-only generic-widget panel for a roll-up.
            restrictToRollup={isRollup}
          />
          {/* Widget settings float in the same slot as the category picker. */}
          <FloatingWidgetSettings
            selectedWidget={selectedWidget}
            onWidgetUpdate={handleWidgetUpdate}
            onDeselectWidget={() => deselectWidgetRef.current()}
            reportDateRangeStart={dateRange.state.start}
            reportDateRangeEnd={dateRange.state.end}
            selectedClientName={client?.name}
            clientId={effectiveClientId}
          />
          </ReportDndProvider>
        </div>

        {/* ── Right: widget category icon column (all category panels float over the canvas) ───── */}
        <aside data-testid="editor-right-sidebar" className="shrink-0 overflow-y-auto bg-background">
          <EditorRightSidebar
            activeCategory={activeCategory}
            setActiveCategory={handleSetActiveCategory}
            setSelectedIntegration={setSelectedIntegration}
            connectedIntegrations={connectedPlatforms}
            // Story 40-6 (AC6): hide the non-aggregatable category icons for a roll-up.
            restrictToRollup={isRollup}
          />
        </aside>

      </div>

      {/* Unsaved changes exit dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
            <DialogDescription>
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExitDialog(false)}>
              Stay
            </Button>
            <Button variant="destructive" onClick={handleConfirmExit} data-testid="confirm-exit-btn">
              Leave Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zoom controls — fixed, centered on the canvas area */}
      <ZoomControls
        zoomLevel={canvasZoom.displayZoom}
        onZoomIn={canvasZoom.zoomIn}
        onZoomOut={canvasZoom.zoomOut}
        onFitToWidth={computeFitToWidth}
        minZoom={ZOOM_MIN}
        maxZoom={ZOOM_MAX}
        canvasLeftOffset={leftWidth}
        canvasRightOffset={rightColumnWidth}
      />
    </>
  );
}
